import {
  validateAppointmentStepOneBookingDraft,
  type AppointmentBookingDraftV1,
  type AppointmentServiceLineDraft,
} from "@/app/booking/core/appointmentBookingDraft";
import { createSpecialistServicesAppointmentDraft } from "@/app/booking/adapters/specialistServicesAppointmentDraft";
import {
  getSpecialistAssignmentByRoute,
  getSpecialistOfferingsForAssignment,
  getSpecialistPerson,
  getSpecialistServicesForAssignment,
  getSpecialistWorkplace,
} from "@/app/specialists/catalog";
import type {
  SpecialistAssignment,
  SpecialistPerson,
  SpecialistService,
  SpecialistServiceOffering,
  SpecialistServiceSelection,
  SpecialistWorkplace,
} from "@/app/specialists/model";
import { getSpecialistWorkplacePresentation } from "@/app/specialists/profilePresentation";

export type SpecialistAppointmentSchedulingContextFailureReason =
  | "assignment-hidden"
  | "assignment-not-found"
  | "catalog-drift"
  | "invalid-draft"
  | "unsupported-entry-point";

export interface SpecialistAppointmentSchedulingCatalogSource {
  getAssignmentByRoute: (
    locationId: string,
    userLocationId: string,
  ) => SpecialistAssignment | undefined;
  getPerson: (personId: string) => SpecialistPerson | undefined;
  getWorkplace: (locationId: string) => SpecialistWorkplace | undefined;
  getServicesForAssignment: (
    userLocationId: string,
  ) => readonly SpecialistService[];
  getOfferingsForAssignment: (
    userLocationId: string,
  ) => readonly SpecialistServiceOffering[];
}

export interface SpecialistAppointmentSchedulingSpecialistPresentation {
  personId: string;
  name: string;
  avatarSrc: string | null;
  avatarAlt: string;
  role: string | null;
  category: string;
  reviewSummary: {
    averageRating: number | null;
    count: number;
  };
}

export interface SpecialistAppointmentSchedulingWorkplacePresentation {
  locationId: string;
  name: string;
  address: string;
  city: string;
  state: string;
  countryCode: string;
  marketplace: ReturnType<typeof getSpecialistWorkplacePresentation>;
}

export type SpecialistAppointmentSchedulingContextResult =
  | {
      ok: true;
      scheduling: {
        draft: AppointmentBookingDraftV1;
        assignment: SpecialistAssignment;
        workplace: SpecialistWorkplace;
        services: readonly SpecialistService[];
        offerings: readonly SpecialistServiceOffering[];
      };
      presentation: {
        specialist: SpecialistAppointmentSchedulingSpecialistPresentation;
        workplace: SpecialistAppointmentSchedulingWorkplacePresentation;
      };
    }
  | {
      ok: false;
      reason: SpecialistAppointmentSchedulingContextFailureReason;
    };

function hasUnresolvedSpecialistSchedulingTask(
  draft: AppointmentBookingDraftV1,
) {
  return draft.participants.some((participant) =>
    participant.serviceLines.some(
      (line) =>
        line.staffPreference.kind === "unresolved" ||
        (line.selection.kind === "bundle" &&
          line.selection.bundleItems.some(
            (item) => item.staffPreference.kind === "unresolved",
          )),
    ),
  );
}

const specialistAppointmentSchedulingFixtureCatalog: SpecialistAppointmentSchedulingCatalogSource =
  {
    getAssignmentByRoute: getSpecialistAssignmentByRoute,
    getPerson: getSpecialistPerson,
    getWorkplace: getSpecialistWorkplace,
    getServicesForAssignment: getSpecialistServicesForAssignment,
    getOfferingsForAssignment: getSpecialistOfferingsForAssignment,
  };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasSameStructure(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true;
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) =>
        hasSameStructure(value, right[index]),
      )
    );
  }

  if (!isRecord(left) || !isRecord(right)) {
    return false;
  }

  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();

  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key, index) =>
        key === rightKeys[index] &&
        hasSameStructure(left[key], right[key]),
    )
  );
}

function getSelectionFromDraftLine(
  line: AppointmentServiceLineDraft,
): SpecialistServiceSelection {
  if (line.selection.kind === "service") {
    return {
      serviceId: line.selection.serviceId,
      variantId: line.selection.variantId,
      addOns: line.selection.addOns.map((addOn) => ({ ...addOn })),
    };
  }

  return {
    serviceId: line.selection.serviceId,
    variantId: null,
    bundleItems: line.selection.bundleItems.flatMap((item) =>
      item.addOns.length > 0
        ? [
            {
              bundleItemId: item.bundleItemId,
              serviceId: item.serviceId,
              variantId: item.variantId,
              addOns: item.addOns.map((addOn) => ({ ...addOn })),
            },
          ]
        : [],
    ),
  };
}

function hasCompleteAssignmentCatalog(input: {
  assignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  services: readonly SpecialistService[];
  offerings: readonly SpecialistServiceOffering[];
}) {
  const { assignment, workplace, services, offerings } = input;
  const assignmentServiceIds = new Set(assignment.serviceIds);
  const serviceIds = services.map(({ serviceId }) => serviceId);
  const offeringServiceIds = offerings.map(({ serviceId }) => serviceId);
  const offeringIds = offerings.map(({ offeringId }) => offeringId);

  return (
    workplace.locationId === assignment.locationId &&
    assignmentServiceIds.size === assignment.serviceIds.length &&
    services.length === assignment.serviceIds.length &&
    offerings.length === assignment.serviceIds.length &&
    new Set(serviceIds).size === serviceIds.length &&
    new Set(offeringServiceIds).size === offeringServiceIds.length &&
    new Set(offeringIds).size === offeringIds.length &&
    serviceIds.every((serviceId) => assignmentServiceIds.has(serviceId)) &&
    offerings.every(
      (offering) =>
        assignmentServiceIds.has(offering.serviceId) &&
        offering.userLocationId === assignment.userLocationId &&
        offering.offeringId.trim().length > 0,
    )
  );
}

function cloneWorkplaceMarketplacePresentation(
  workplace: SpecialistWorkplace,
) {
  const presentation = getSpecialistWorkplacePresentation(workplace);

  if (!presentation) {
    return null;
  }

  return {
    ...presentation,
    images: presentation.images?.map((image) => ({ ...image })),
  };
}

/**
 * Resolves a stored Specialist-origin Step-1 draft against the current
 * assignment/catalog before scheduling can consume it. The draft is rebuilt
 * from exact catalog identifiers and compared atomically, so no stale line,
 * snapshot, policy, quote, timing, staff, or bundle child can survive as a
 * partial context.
 *
 * This adapter intentionally accepts no URL compatibility state. The
 * validated AppointmentBookingDraft is the complete Step-1 handoff.
 */
export function resolveSpecialistAppointmentSchedulingContext(
  draft: AppointmentBookingDraftV1,
  catalog: SpecialistAppointmentSchedulingCatalogSource =
    specialistAppointmentSchedulingFixtureCatalog,
): SpecialistAppointmentSchedulingContextResult {
  const validation = validateAppointmentStepOneBookingDraft(draft);

  if (!validation.valid) {
    return { ok: false, reason: "invalid-draft" };
  }

  const validatedDraft = validation.draft;

  if (validatedDraft.entryPoint.kind !== "specialist-services") {
    return { ok: false, reason: "unsupported-entry-point" };
  }

  // Specialist-origin booking intentionally has no Professional stage. A
  // parallel bundle may retain one exact entry-specialist anchor in Step 1,
  // but any simultaneous unresolved sibling must fail before scheduling.
  if (hasUnresolvedSpecialistSchedulingTask(validatedDraft)) {
    return { ok: false, reason: "invalid-draft" };
  }

  const { locationId, userLocationId } = validatedDraft.entryPoint;
  const assignment = catalog.getAssignmentByRoute(
    locationId,
    userLocationId,
  );

  if (!assignment) {
    return { ok: false, reason: "assignment-not-found" };
  }

  if (
    assignment.locationId !== locationId ||
    assignment.userLocationId !== userLocationId
  ) {
    return { ok: false, reason: "catalog-drift" };
  }

  if (!assignment.isPublicProfileVisible) {
    return { ok: false, reason: "assignment-hidden" };
  }

  const person = catalog.getPerson(assignment.personId);
  const workplace = catalog.getWorkplace(locationId);
  const services = catalog.getServicesForAssignment(userLocationId);
  const offerings = catalog.getOfferingsForAssignment(userLocationId);

  if (
    !person ||
    person.personId !== assignment.personId ||
    !workplace ||
    !hasCompleteAssignmentCatalog({
      assignment,
      workplace,
      services,
      offerings,
    })
  ) {
    return { ok: false, reason: "catalog-drift" };
  }

  const organizer = validatedDraft.participants.find(
    ({ participantId }) =>
      participantId === validatedDraft.organizerParticipantId,
  );

  if (!organizer) {
    return { ok: false, reason: "catalog-drift" };
  }

  const selections = [...organizer.serviceLines]
    .sort((left, right) => left.selectionOrder - right.selectionOrder)
    .map(getSelectionFromDraftLine);
  const groupGuests = validatedDraft.participants
    .filter(({ participantId }) => participantId !== organizer.participantId)
    .map((participant) => ({
      participantId: participant.participantId,
      selections: [...participant.serviceLines]
        .sort((left, right) => left.selectionOrder - right.selectionOrder)
        .map(getSelectionFromDraftLine),
    }));
  const rebuilt = createSpecialistServicesAppointmentDraft({
    draftId: validatedDraft.draftId,
    mode: validatedDraft.mode,
    assignment,
    workplace,
    services,
    offerings,
    selections,
    groupGuests,
  });

  if (
    !rebuilt.ok ||
    !hasSameStructure(validatedDraft, rebuilt.draft)
  ) {
    return { ok: false, reason: "catalog-drift" };
  }

  return {
    ok: true,
    scheduling: {
      draft: rebuilt.draft,
      assignment,
      workplace,
      services,
      offerings,
    },
    presentation: {
      specialist: {
        personId: person.personId,
        name: person.name,
        avatarSrc: person.avatarSrc,
        avatarAlt: person.avatarAlt,
        role: person.role,
        category: person.category,
        reviewSummary: { ...person.reviewSummary },
      },
      workplace: {
        locationId: workplace.locationId,
        name: workplace.name,
        address: workplace.address,
        city: workplace.city,
        state: workplace.state,
        countryCode: workplace.countryCode,
        marketplace: cloneWorkplaceMarketplacePresentation(workplace),
      },
    },
  };
}
